import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { NaviContext } from "./navi";
import { firestore } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";

/**
 * Role constants matching backend definitions
 */
export const ROLES = {
  SUPERADMIN: "superadmin",
  ADMIN: "admin",
  INVESTOR: "investor",
  PROJECT_OWNER: "projectOwner",
};

export const ROLE_DISPLAY_NAMES = {
  [ROLES.SUPERADMIN]: "Super Administrator",
  [ROLES.ADMIN]: "Administrator",
  [ROLES.INVESTOR]: "Investor",
  [ROLES.PROJECT_OWNER]: "Project Owner",
};

export const ROLE_PERMISSIONS = {
  [ROLES.SUPERADMIN]: ["projects", "portfolio", "admin", "all-projects"],
  [ROLES.ADMIN]: ["projects", "portfolio", "admin"],
  [ROLES.PROJECT_OWNER]: ["projects", "portfolio"],
  [ROLES.INVESTOR]: ["portfolio"],
};

/**
 * Context for role-based access control
 */
export const RolesContext = createContext({
  roles: [],
  loading: true,
  isSuperadmin: false,
  isAdmin: false,
  isInvestor: false,
  isProjectOwner: false,
  hasRole: () => false,
  hasAnyRole: () => false,
  canAccess: () => false,
  getAccessibleSections: () => [],
  refreshRoles: () => Promise.resolve(),
  hasNoRoles: false,
});

/**
 * Provider component for role-based access control
 *
 * Provides role information throughout the app via context.
 * Automatically refreshes roles when roleUpdatedAt changes in Firestore.
 */
export function RolesProvider({ children }) {
  const { context } = useContext(NaviContext);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRoleUpdate, setLastRoleUpdate] = useState(null);

  // Fetch roles from ID token
  const fetchRoles = useCallback(async (forceRefresh = false) => {
    if (context?.user) {
      try {
        const result = await context.user.getIdTokenResult(forceRefresh);
        setRoles(result.claims.roles || []);
      } catch (error) {
        console.error("Error fetching roles:", error);
        setRoles([]);
      }
      setLoading(false);
    } else {
      setRoles([]);
      setLoading(false);
    }
  }, [context?.user]);

  // Initial role fetch
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Subscribe to roleUpdatedAt changes in Firestore to force token refresh
  useEffect(() => {
    if (!context?.user?.uid) {
      return;
    }

    const userDocRef = doc(firestore, "investors", context.user.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const roleUpdatedAt = data?.roleUpdatedAt?.toMillis?.() || null;

          // If roleUpdatedAt changed, force token refresh
          if (roleUpdatedAt && lastRoleUpdate && roleUpdatedAt > lastRoleUpdate) {
            fetchRoles(true);
          }
          setLastRoleUpdate(roleUpdatedAt);
        }
      },
      (error) => {
        console.error("Error subscribing to role updates:", error);
      }
    );

    return unsubscribe;
  }, [context?.user?.uid, lastRoleUpdate, fetchRoles]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role) => roles.includes(role),
    [roles]
  );

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback(
    (...checkRoles) => checkRoles.some((r) => roles.includes(r)),
    [roles]
  );

  /**
   * Check if user can access a specific section
   */
  const canAccess = useCallback(
    (section) => {
      return roles.some((role) => ROLE_PERMISSIONS[role]?.includes(section));
    },
    [roles]
  );

  /**
   * Get all accessible sections for current user
   */
  const getAccessibleSections = useCallback(() => {
    const sections = new Set();
    roles.forEach((role) => {
      const perms = ROLE_PERMISSIONS[role];
      if (perms) {
        perms.forEach((section) => sections.add(section));
      }
    });
    return Array.from(sections);
  }, [roles]);

  /**
   * Force refresh roles from server
   */
  const refreshRoles = useCallback(() => fetchRoles(true), [fetchRoles]);

  const isSuperadmin = roles.includes(ROLES.SUPERADMIN);

  const value = {
    roles,
    loading,
    isSuperadmin,
    isAdmin: roles.includes(ROLES.ADMIN) || isSuperadmin,
    isInvestor: roles.includes(ROLES.INVESTOR),
    isProjectOwner: roles.includes(ROLES.PROJECT_OWNER),
    hasRole,
    hasAnyRole,
    canAccess,
    getAccessibleSections,
    refreshRoles,
    hasNoRoles: roles.length === 0 && !loading,
  };

  return (
    <RolesContext.Provider value={value}>
      {children}
    </RolesContext.Provider>
  );
}

/**
 * Hook to access roles context
 */
export function useRolesContext() {
  return useContext(RolesContext);
}

export default RolesContext;
